import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import DayPicker, { DateUtils } from 'react-day-picker';
import 'react-day-picker/lib/style.css';

import * as appActions from '../actions/appActions';
import * as indicesActions from '../actions/indicesActions';
import { getMarketsListWithProducts } from '../actions/indicesActions';

import { renderYearsArray, getWeekdays } from '../services/utils';
import appConfig from '../config/app-config';
import { getMarketsList } from '../selectors/indicesSelectors';
import { IndicesFrequency } from '../constants/indices';

const { initialYear } = appConfig;
const currentYear = new Date().getFullYear();

const Rules = {
    EACH: 'each',
    FIRST: 'first',
    LAST: 'last',
};

const DEFAULT_WEEKDAY = '1';

const getWorkingDays = (rule, year) =>
    [1, 2, 3, 4, 5].reduce((agg, weekday) => {
        const dates = getWeekdays(weekday, rule, year);

        return [...agg, ...dates];
    }, []);

class ScreenComponent extends React.Component {
    state = {
        selectedDays: [],
        blank: true,
        selectedMonth: 0,
        marketId: '',
        year: currentYear,
        rule: Rules.EACH,
        weekday: DEFAULT_WEEKDAY,
    };

    componentDidMount() {
        if (!this.props.markets.length) {
            this.props.getMarketsListWithProducts();
        }
        this.updateData(this.props);
    }

    componentWillReceiveProps(nextProps) {
        this.updateData(nextProps);
    }

    updateData = props => {
        const { markets } = props;

        if (markets.length && !this.state.marketId) {
            const market = markets[0];
            this.setState({
                ...this.state,
                marketId: market.marketId,
            });
            this.getDates(markets[0].id, this.state.year, markets[0].frequency_type);
        }
    };

    handleChange = e => {
        const { name, value } = e.target;

        const marketId = name === 'marketId' ? value : this.state.marketId;
        const market = this.props.markets.find(m => m.marketId === marketId);

        this.setState({
            weekday: market.frequency_type === IndicesFrequency.DAILY ? '' : DEFAULT_WEEKDAY,
            [name]: value,
        });

        if (name === 'marketId') {
            this.getDates(market.id, this.state.year, market.frequency_type);
        }
        if (name === 'year') {
            this.getDates(market.id, value, market.frequency_type);
        }
    };

    getDates = (marketId, year, frequency_type) => {
        const setDates = dates => {
            this.setState({
                ...this.state,
                blank: !dates.length,
                selectedDays: dates.map(date => new Date(date)),
            });
        };
        this.props.getScheduleV2(marketId, year, frequency_type, setDates);
    };

    handleAutofill = () => {
        const { weekday, rule, year } = this.state;
        const dates = weekday === '' ? getWorkingDays(rule, year) : getWeekdays(weekday, rule, year);

        this.setState({
            ...this.state,
            selectedDays: dates,
        });
    };

    handleDayClick = (day, { selected }) => {
        const { selectedDays } = this.state;
        if (selected) {
            const selectedIndex = selectedDays.findIndex(selectedDay => DateUtils.isSameDay(selectedDay, day));
            selectedDays.splice(selectedIndex, 1);
        } else {
            selectedDays.push(day);
        }
        this.setState({ selectedDays });
    };

    handleMonthChange = date => {
        const selectedMonth = new Date(date).getMonth();
        this.setState({
            ...this.state,
            selectedMonth,
        });
    };

    saveSchedule = () => {
        const { marketId, year, selectedDays, blank } = this.state;
        const market = this.props.markets.find(m => m.marketId === marketId);
        this.props.saveSchedule(+market.id, +year, selectedDays, market.frequency_type, blank);
        this.props.scheduleShouldBeUpdated(true);
    };

    render() {
        const selectedMarket = this.props.markets.find(m => m.marketId === this.state.marketId);
        const isDailySchedule = selectedMarket && selectedMarket.frequency_type === IndicesFrequency.DAILY;

        return (
            <div className="indices-schedule-holder">
                <h1>Manage indices schedule</h1>
                <div className="options-holder">
                    <div className="options-column market-selector">
                        <div className="label">Select market:</div>
                        <select onChange={this.handleChange} name="marketId" value={this.state.marketId}>
                            {this.props.markets.length
                                ? this.props.markets.map(market => (
                                      <option key={`marksel_${market.marketId}`} value={market.marketId}>
                                          {market.name} 
                                      </option>
                                  ))
                                : null}
                        </select>
                    </div>
                    <div className="options-column">
                        <div className="label">Select a year:</div>
                        <select onChange={this.handleChange} name="year" value={this.state.year}>
                            {renderYearsArray(initialYear, 3).map(year => (
                                <option key={`year${year}`} value={year}>
                                    {year}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="options-holder">
                    <div className="label">Set rule and click autofill to fill in index days:</div>
                    <select onChange={this.handleChange} name="rule" value={this.state.rule} disabled={isDailySchedule}>
                        {isDailySchedule ? (
                            <option value={Rules.EACH}>{Rules.EACH}</option>
                        ) : (
                            Object.values(Rules).map((rule, i) => (
                                <option key={`rule_${i}`} value={rule}>
                                    {rule}
                                </option>
                            ))
                        )}
                    </select>
                    <select
                        onChange={this.handleChange}
                        name="weekday"
                        className="weekday-selector"
                        value={this.state.weekday}
                        disabled={isDailySchedule}
                    >
                        {isDailySchedule ? (
                            <option value="">Working Day</option>
                        ) : (
                            <React.Fragment>
                                <option value={1}>Monday</option>
                                <option value={2}>Tuesday</option>
                                <option value={3}>Wednesday</option>
                                <option value={4}>Thursday</option>
                                <option value={5}>Friday</option>
                                <option value={6}>Saturday</option>
                                <option value={0}>Sunday</option>
                            </React.Fragment>
                        )}
                    </select>
                    <div className="inline-label">in month</div>
                    <button type="button" onClick={this.handleAutofill}>
                        Autofill
                    </button>
                </div>

                <DayPicker
                    firstDayOfWeek={1}
                    month={new Date(this.state.year, this.state.selectedMonth)}
                    fromMonth={new Date(this.state.year, 0)}
                    toMonth={new Date(this.state.year, 11)}
                    selectedDays={this.state.selectedDays}
                    onMonthChange={this.handleMonthChange}
                    onDayClick={this.handleDayClick}
                />

                <div className="saveSchedule-holder">
                    <button className="button" type="submit" onClick={this.saveSchedule}>
                        Save schedule
                    </button>
                </div>
            </div>
        );
    }
}

ScreenComponent.propTypes = {
    getMarketsListWithProducts: PropTypes.func.isRequired,
    getScheduleV2: PropTypes.func.isRequired,
    scheduleShouldBeUpdated: PropTypes.func.isRequired,
    saveSchedule: PropTypes.func.isRequired,

    markets: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

export default connect(
    state => ({
        indices: state.indices,
        markets: getMarketsList(state),
    }),
    dispatch => ({
        ...bindActionCreators({ ...appActions, ...indicesActions, getMarketsListWithProducts }, dispatch),
    }),
)(ScreenComponent);



// WEBPACK FOOTER //
// ./source/screens/ManageIndicesSchedule.js